import { cva, type VariantProps } from 'class-variance-authority';
import * as React from 'react';
import { cn } from 'v5/platform/dom/cn';

const alertVariants = cva(
  'relative w-full rounded-lg border [&>svg]:absolute [&>svg]:size-7 [&>svg]:text-fg-default',
  {
    variants: {
      variant: {
        default: 'bg-bg-default text-fg-default',
        destructive:
          'bg-bg-danger/40 text-fg-danger [&>svg]:text-fg-danger border-none'
      },
      layout: {
        default: [
          '[&>svg]:size-7 ',
          'p-4 [&>svg~*]:pl-10 [&>svg+div]:translate-y-[-3px] [&>svg]:left-4 [&>svg]:top-5 [&_[data-element=alert-title]]:mb-1'
        ],
        horizontal: [
          '[&>svg]:size-5 ',
          'px-4 py-2 flex items-center gap-4 [&>svg]:static shrink-0 *:w-min-0'
        ]
      }
    },
    defaultVariants: {
      variant: 'default',
      layout: 'default'
    }
  }
);

const Alert = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement> & VariantProps<typeof alertVariants>
>(({ className, variant, layout, ...props }, ref) => (
  <div
    ref={ref}
    role="alert"
    className={cn(alertVariants({ variant, layout }), className)}
    {...props}
  />
));
Alert.displayName = 'Alert';

const AlertTitle = React.forwardRef<
  HTMLParagraphElement,
  React.HTMLAttributes<HTMLHeadingElement>
>(({ className, ...props }, ref) => (
  <div
    ref={ref}
    data-element="alert-title"
    className={cn(
      'text-lg font-semibold leading-none tracking-tight',
      className
    )}
    {...props}
  />
));
AlertTitle.displayName = 'AlertTitle';

const AlertDescription = React.forwardRef<
  HTMLParagraphElement,
  React.HTMLAttributes<HTMLParagraphElement>
>(({ className, ...props }, ref) => (
  <div
    ref={ref}
    className={cn('text-base [&_p]:leading-relaxed', className)}
    {...props}
  />
));
AlertDescription.displayName = 'AlertDescription';

export { Alert, AlertTitle, AlertDescription };
